import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Select from '@orn/shared-web/components/Select';
import { ChevronDownIcon, ChevronRightIcon } from '@orn/shared-web/icons';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';

import { PaginationDirection, ProcessorStatus } from '../../__generated__/globalTypes';
import AdminSelectUser from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Table from '../../components/Table';

import type {
  AdminGetAllWebhookCodesQuery,
  AdminGetProcessorStatusesQuery,
  AdminGetProcessorStatusesQueryVariables,
  AdminUpdateProcessorStatusMutation,
  AdminUpdateProcessorStatusMutationVariables,
} from './__generated__/ProcessorStatusesScene.gql';

const PAGE_SIZE = 25;

const UPDATE_PROCESSOR_STATUS = gql`
  mutation AdminUpdateProcessorStatus($processorStatusId: ID!, $status: ProcessorStatus!) {
    adminUpdateProcessorStatus(processorStatusId: $processorStatusId, status: $status) {
      id
      status
    }
  }
`;

const ADMIN_GET_PROCESSOR_STATUSES = gql`
  query AdminGetProcessorStatuses($pagination: PaginationInput!, $filters: ProcessorStatusFilters) {
    adminGetProcessorStatuses(pagination: $pagination, filters: $filters) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          externalBankConnectionId
          webhookCode
          startTime
          finishTime
          status
          errorMessage
          rawJson
          user {
            id
            name
            email
          }
          bankConnection {
            id
            externalId
            bank {
              id
              name
              logoUrl
            }
          }
        }
      }
    }
  }
`;

const ADMIN_GET_ALL_WEBHOOK_CODES = gql`
  query AdminGetAllWebhookCodes {
    adminGetAllWebhookCodes
  }
`;

type StatusFilter = 'ALL' | ProcessorStatus;

type ProcessorStatusType = NonNullable<
  AdminGetProcessorStatusesQuery['adminGetProcessorStatuses']
>['edges'][number]['node'];

// `bg-X-primary` resolves to the 950 shade and `text-fg-X-primary` to the 500
// shade, giving the dark-tinted-bg / saturated-fg badge treatment. The brand
// pair is the exception — both tokens collapse to brand-500 in dark mode (an
// upstream theme.css quirk), so we use the utility-brand stops directly to get
// the same dark-bg + bright-fg shape for the in-flight badge.
const STATUS_BADGE_COLOR: Record<ProcessorStatus, string> = {
  [ProcessorStatus.Running]: 'bg-utility-brand-50 text-utility-brand-700',
  [ProcessorStatus.Completed]: 'bg-success-primary text-fg-success-primary',
  [ProcessorStatus.Failed]: 'bg-error-primary text-fg-error-primary',
  [ProcessorStatus.FailedResolved]: 'bg-warning-primary text-fg-warning-primary',
  [ProcessorStatus.Skipped]: 'bg-bg-secondary text-fg-secondary',
};

/**
 * Format the elapsed time between a processor status's start and finish
 * timestamps into a compact `Hh Mm Ss` / `Mm Ss` / `Ss` string. Returns
 * `Running...` while still in flight (no finishTime yet).
 */
export function formatProcessorDuration(startTime: string, finishTime: string | null | undefined): string {
  if (!finishTime) {
    return 'Running...';
  }
  const start = new Date(startTime).getTime();
  const finish = new Date(finishTime).getTime();
  const durationMs = finish - start;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

interface IBaseProcessorStatusesPaneProps {
  processorStatuses: ProcessorStatusType[];
  statusFilter: StatusFilter;
  userIdFilter: string | null;
  webhookCodeFilter: string | null;
  webhookCodes: string[];
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoading: boolean;
  onStatusFilterChange: (status: StatusFilter) => void;
  onUserIdFilterChange: (userId: string | null) => void;
  onWebhookCodeFilterChange: (webhookCode: string | null) => void;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  onResolve: (processorStatusId: string) => Promise<void>;
}

/**
 * Pure presentational shell for the Processor Statuses tool. All state — filter
 * selections, current page, expand state for raw JSON / error rows — is owned
 * by this component or driven by callbacks; data is fetched by the container
 * (`ProcessorStatusesScene`).
 */
export const BaseProcessorStatusesPane = ({
  processorStatuses,
  statusFilter,
  userIdFilter,
  webhookCodeFilter,
  webhookCodes,
  currentPage,
  hasNextPage,
  hasPreviousPage,
  isLoading,
  onStatusFilterChange,
  onUserIdFilterChange,
  onWebhookCodeFilterChange,
  onClearFilters,
  onPageChange,
  onResolve,
}: IBaseProcessorStatusesPaneProps) => {
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const count = processorStatuses.length;
  const filtersActive = statusFilter !== 'ALL' || userIdFilter !== null || webhookCodeFilter !== null;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Processor statuses ({count})</h1>

        {/* Filters */}
        <div className="border-border-primary flex flex-wrap items-end gap-4 border-b pb-4">
          <div className="flex flex-col gap-1">
            <label className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">User</label>
            <AdminSelectUser
              selectedUserId={userIdFilter}
              onSelectionChange={onUserIdFilterChange}
              placeholder="Select a user to filter…"
              hideLabel
              className="w-80"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Webhook code</label>
            <Select
              placeholder="All webhook codes"
              value={webhookCodeFilter}
              onChange={(key) => onWebhookCodeFilterChange(key ? (key as string) : null)}
              items={webhookCodes.map((code) => ({ id: code, label: code }))}
              className="w-64"
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Status</label>
            <Select
              placeholder="All statuses"
              value={statusFilter === 'ALL' ? null : statusFilter}
              onChange={(key) => onStatusFilterChange(key ? (key as StatusFilter) : 'ALL')}
              items={[
                { id: ProcessorStatus.Running, label: 'Running' },
                { id: ProcessorStatus.Skipped, label: 'Skipped' },
                { id: ProcessorStatus.Completed, label: 'Completed' },
                { id: ProcessorStatus.Failed, label: 'Failed' },
                { id: ProcessorStatus.FailedResolved, label: 'Failed (resolved)' },
              ]}
              className="w-48"
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </div>

          {filtersActive && (
            <Button onClick={onClearFilters} color="secondary" size="sm">
              Clear filters
            </Button>
          )}
        </div>

        {isLoading ? (
          <span className="text-fg-secondary text-xs">Loading…</span>
        ) : count === 0 ? (
          <div className="text-fg-tertiary flex h-32 items-center justify-center text-xs italic">
            No processor statuses match the current filters.
          </div>
        ) : (
          <Table>
            <Table.Header sticky>
              <Table.Row>
                <Table.HeaderCell />
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Webhook code</Table.HeaderCell>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Bank connection</Table.HeaderCell>
                <Table.HeaderCell>Start</Table.HeaderCell>
                <Table.HeaderCell>Duration</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {processorStatuses.map((processorStatus) => {
                const isExpanded = expandedRows.has(processorStatus.id);
                const hasRawJson = processorStatus.rawJson != null;
                const hasErrorMessage = Boolean(processorStatus.errorMessage);

                return (
                  <React.Fragment key={processorStatus.id}>
                    <Table.Row className="cursor-pointer" onClick={() => toggleRow(processorStatus.id)}>
                      <Table.Cell muted>
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <span
                          className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${STATUS_BADGE_COLOR[processorStatus.status]}`}
                        >
                          {processorStatus.status}
                        </span>
                      </Table.Cell>
                      <Table.Cell mono>{processorStatus.webhookCode}</Table.Cell>
                      <Table.Cell className="whitespace-nowrap" title={processorStatus.user?.email ?? undefined}>
                        {processorStatus.user ? (
                          (processorStatus.user.name ?? processorStatus.user.email)
                        ) : (
                          <span className="text-fg-tertiary italic">No user</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap" title={processorStatus.externalBankConnectionId}>
                        <span className="inline-flex items-center gap-2">
                          {processorStatus.bankConnection?.bank.logoUrl && (
                            <img
                              src={processorStatus.bankConnection.bank.logoUrl}
                              alt={processorStatus.bankConnection.bank.name}
                              className="border-border-primary h-4 w-4 shrink-0 rounded border"
                            />
                          )}
                          <span>{processorStatus.bankConnection?.bank.name ?? 'Unknown bank'}</span>
                        </span>
                      </Table.Cell>
                      <Table.Cell
                        mono
                        muted
                        className="whitespace-nowrap"
                        title={new Date(processorStatus.startTime).toLocaleString()}
                      >
                        {formatTimeAgo(processorStatus.startTime)}
                      </Table.Cell>
                      <Table.Cell mono muted>
                        {formatProcessorDuration(processorStatus.startTime, processorStatus.finishTime)}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {processorStatus.status === ProcessorStatus.Failed && (
                            <Button
                              onClick={() => {
                                void onResolve(processorStatus.id);
                              }}
                              color="primary"
                              size="xs"
                            >
                              Resolve
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    {isExpanded && (
                      <Table.Row>
                        <Table.Cell className="bg-bg-secondary" colSpan={8}>
                          <div className="flex flex-col gap-3">
                            <div className="grid grid-cols-[max-content_1fr] gap-3">
                              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 self-start text-xs">
                                <dt className="text-fg-tertiary uppercase tracking-wider">Status ID</dt>
                                <dd className="text-fg-secondary font-mono">{processorStatus.id}</dd>
                                <dt className="text-fg-tertiary uppercase tracking-wider">Started</dt>
                                <dd className="text-fg-secondary font-mono">
                                  {new Date(processorStatus.startTime).toLocaleString()}
                                </dd>
                                {processorStatus.finishTime && (
                                  <>
                                    <dt className="text-fg-tertiary uppercase tracking-wider">Finished</dt>
                                    <dd className="text-fg-secondary font-mono">
                                      {new Date(processorStatus.finishTime).toLocaleString()}
                                    </dd>
                                  </>
                                )}
                                {processorStatus.user && (
                                  <>
                                    <dt className="text-fg-tertiary uppercase tracking-wider">User email</dt>
                                    <dd className="text-fg-secondary font-mono">{processorStatus.user.email}</dd>
                                    <dt className="text-fg-tertiary uppercase tracking-wider">User ID</dt>
                                    <dd className="text-fg-secondary font-mono">{processorStatus.user.id}</dd>
                                  </>
                                )}
                                <dt className="text-fg-tertiary uppercase tracking-wider">External connection</dt>
                                <dd className="text-fg-secondary font-mono">
                                  {processorStatus.externalBankConnectionId}
                                </dd>
                                {processorStatus.bankConnection && (
                                  <>
                                    <dt className="text-fg-tertiary uppercase tracking-wider">Connection ID</dt>
                                    <dd className="text-fg-secondary font-mono">{processorStatus.bankConnection.id}</dd>
                                    <dt className="text-fg-tertiary uppercase tracking-wider">Bank ID</dt>
                                    <dd className="text-fg-secondary font-mono">
                                      {processorStatus.bankConnection.bank.id}
                                    </dd>
                                  </>
                                )}
                              </dl>
                              {hasErrorMessage && (
                                <div className="self-start">
                                  <div className="text-fg-tertiary mb-1 text-xs font-medium uppercase tracking-wider">
                                    Error
                                  </div>
                                  <pre className="text-fg-error-primary m-0 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-xs">
                                    {processorStatus.errorMessage}
                                  </pre>
                                </div>
                              )}
                            </div>
                            {hasRawJson && (
                              <div className="border-border-primary rounded border p-3">
                                <div className="text-fg-tertiary mb-1 text-xs font-medium uppercase tracking-wider">
                                  Raw JSON
                                </div>
                                <pre className="m-0 max-h-96 overflow-auto whitespace-pre-wrap font-mono text-[10px] leading-snug">
                                  {JSON.stringify(processorStatus.rawJson, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Body>
          </Table>
        )}

        {count > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <Button onClick={() => onPageChange(1)} isDisabled={currentPage === 1} color="secondary" size="sm">
              First
            </Button>
            <Button
              onClick={() => onPageChange(currentPage - 1)}
              isDisabled={!hasPreviousPage}
              color="secondary"
              size="sm"
            >
              Previous
            </Button>
            <span className="text-fg-secondary">Page {currentPage}</span>
            <Button onClick={() => onPageChange(currentPage + 1)} isDisabled={!hasNextPage} color="secondary" size="sm">
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

/**
 * Processor Statuses admin tool. Lists every processor execution captured from
 * Plaid webhooks with its status, user, bank connection, and (on expand) the
 * full error/raw-JSON payload. Supports filtering by user, webhook code, and
 * status; resolving failed runs marks them as `FAILED_RESOLVED`.
 *
 * Container split: `ProcessorStatusesScene` owns Apollo + paging state;
 * `BaseProcessorStatusesPane` is pure presentational (testable in isolation).
 */
const ProcessorStatusesScene = () => {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('ALL');
  const [userIdFilter, setUserIdFilter] = React.useState<string | null>(null);
  const [webhookCodeFilter, setWebhookCodeFilter] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentCursor, setCurrentCursor] = React.useState<string | undefined>(undefined);
  const [direction, setDirection] = React.useState<PaginationDirection>(PaginationDirection.Forward);

  const [updateProcessorStatus] = useMutation<
    AdminUpdateProcessorStatusMutation,
    AdminUpdateProcessorStatusMutationVariables
  >(UPDATE_PROCESSOR_STATUS);

  const { data: webhookCodesData } = useQuery<AdminGetAllWebhookCodesQuery>(ADMIN_GET_ALL_WEBHOOK_CODES);
  const webhookCodes = webhookCodesData?.adminGetAllWebhookCodes ?? [];

  const buildFilters = (status: StatusFilter, userId: string | null, webhookCode: string | null) => {
    const filters: { status?: ProcessorStatus; userId?: string; webhookCode?: string } = {};
    if (status !== 'ALL') {
      filters.status = status;
    }
    if (userId) {
      filters.userId = userId;
    }
    if (webhookCode) {
      filters.webhookCode = webhookCode;
    }
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const [executeQuery, { data, loading }] = useLazyQuery<
    AdminGetProcessorStatusesQuery,
    AdminGetProcessorStatusesQueryVariables
  >(ADMIN_GET_PROCESSOR_STATUSES);

  const processorStatuses = data?.adminGetProcessorStatuses?.edges?.map((edge) => edge.node) ?? [];
  const pageInfo = data?.adminGetProcessorStatuses?.pageInfo ?? null;
  const hasNextPage = data?.adminGetProcessorStatuses?.pageInfo?.hasNextPage ?? false;
  const hasPreviousPage = data?.adminGetProcessorStatuses?.pageInfo?.hasPreviousPage ?? false;

  const executeQueryWithCurrentParams = React.useCallback(() => {
    void executeQuery({
      variables: {
        pagination: {
          cursor: currentCursor,
          limit: PAGE_SIZE,
          direction,
        },
        filters: buildFilters(statusFilter, userIdFilter, webhookCodeFilter),
      },
    });
  }, [executeQuery, currentCursor, direction, statusFilter, userIdFilter, webhookCodeFilter]);

  React.useEffect(() => {
    executeQueryWithCurrentParams();
  }, [executeQueryWithCurrentParams]);

  const resetPaging = () => {
    setCurrentPage(1);
    setCurrentCursor(undefined);
    setDirection(PaginationDirection.Forward);
  };

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    resetPaging();
  };

  const handleUserIdFilterChange = (userId: string | null) => {
    setUserIdFilter(userId);
    resetPaging();
  };

  const handleWebhookCodeFilterChange = (webhookCode: string | null) => {
    setWebhookCodeFilter(webhookCode);
    resetPaging();
  };

  const handleClearFilters = () => {
    setStatusFilter('ALL');
    setUserIdFilter(null);
    setWebhookCodeFilter(null);
    resetPaging();
  };

  const handlePageChange = (page: number) => {
    if (page === 1) {
      setCurrentCursor(undefined);
      setDirection(PaginationDirection.Forward);
    } else if (page > currentPage) {
      setCurrentCursor(pageInfo?.endCursor ?? undefined);
      setDirection(PaginationDirection.Forward);
    } else if (page < currentPage) {
      setCurrentCursor(pageInfo?.startCursor ?? undefined);
      setDirection(PaginationDirection.Backward);
    }
    setCurrentPage(page);
  };

  const handleResolve = async (processorStatusId: string) => {
    try {
      await updateProcessorStatus({
        variables: { processorStatusId, status: ProcessorStatus.FailedResolved },
      });
      executeQueryWithCurrentParams();
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    }
  };

  return (
    <BaseProcessorStatusesPane
      processorStatuses={processorStatuses}
      statusFilter={statusFilter}
      userIdFilter={userIdFilter}
      webhookCodeFilter={webhookCodeFilter}
      webhookCodes={webhookCodes}
      currentPage={currentPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      isLoading={loading}
      onStatusFilterChange={handleStatusFilterChange}
      onUserIdFilterChange={handleUserIdFilterChange}
      onWebhookCodeFilterChange={handleWebhookCodeFilterChange}
      onClearFilters={handleClearFilters}
      onPageChange={handlePageChange}
      onResolve={handleResolve}
    />
  );
};

export default ProcessorStatusesScene;
